import React, { useState, useMemo } from 'react';
import skillsData from '../../../data/skills.json';

interface SkillsTabProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedRulebookIds: string[];
}

export const SkillsTab: React.FC<SkillsTabProps> = ({
  searchTerm,
  setSearchTerm,
  selectedRulebookIds
}) => {
  interface Skill {
    id: string;
    name: string;
    characteristic: string;
    category: string;
    shortDescription: string;
    fullDescription: string;
    isSpecialist: boolean;
    rulebook: string;
  }

  const skills = skillsData.skills as Skill[];
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const visibleSkills = useMemo(
    () => skills.filter((skill) => selectedRulebookIds.includes(skill.rulebook)),
    [skills, selectedRulebookIds]
  );

  const categories = useMemo(() => {
    const cats = new Set(visibleSkills.map((s) => s.category));
    return Array.from(cats).sort();
  }, [visibleSkills]);

  const filteredSkills = useMemo(() => {
    let filtered = visibleSkills;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((s) => s.category === selectedCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (skill) =>
          skill.name.toLowerCase().includes(term) ||
          skill.shortDescription.toLowerCase().includes(term) ||
          skill.fullDescription.toLowerCase().includes(term) ||
          skill.characteristic.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [searchTerm, selectedCategory, visibleSkills]);

  const getCharacteristicAbbr = (char: string): string => {
    const map: Record<string, string> = {
      weaponSkill: 'WS',
      ballisticSkill: 'BS',
      strength: 'S',
      toughness: 'T',
      agility: 'Ag',
      intelligence: 'Int',
      perception: 'Per',
      willpower: 'WP',
      fellowship: 'Fel'
    };
    return map[char] || char.substring(0, 3).toUpperCase();
  };

  return (
    <div>
      {/* Filters */}
      <div className="bg-surface-alt rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Search Skills</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, description, or characteristic..."
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-2 text-sm text-ink-dim">
          Showing {filteredSkills.length} of {visibleSkills.length} skills
        </div>
      </div>

      {/* Skills Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSkills.map((skill) => (
          <div
            key={skill.id}
            className="group bg-surface border border-line rounded-lg p-4 hover:shadow-lg transition-shadow"
            title={`${skill.fullDescription}\n\nCharacteristic: ${skill.characteristic}\nCategory: ${skill.category}`}
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-semibold text-ink">{skill.name}</h3>
              <span className="px-2 py-1 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 font-mono">
                {getCharacteristicAbbr(skill.characteristic)}
              </span>
            </div>
            <div className="flex gap-2 mb-2">
              <span className="px-2 py-1 text-xs rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                {skill.category}
              </span>
              {skill.isSpecialist && (
                <span className="px-2 py-1 text-xs rounded bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200">
                  Specialist
                </span>
              )}
            </div>
            <p className="text-ink text-sm">{skill.shortDescription}</p>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredSkills.length === 0 && (
        <div className="text-center py-12">
          <p className="text-ink-faint text-lg">No skills found matching your criteria</p>
        </div>
      )}
    </div>
  );
};
